import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import axiosInstance from '../../api/axiosInstance';
import Swal from 'sweetalert2';

const WhatsappSetting = () => {
    const navigate = useNavigate();
    const [isReady,   setIsReady]   = useState(false);
    const [qr,        setQr]        = useState(null);
    const [loading,   setLoading]   = useState(true);
    const [enabled,   setEnabled]   = useState(false);
    const [toggling,  setToggling]  = useState(false);

    // Fetch module enabled state once on mount
    const fetchModuleStatus = useCallback(async () => {
        try {
            const res = await axiosInstance.get('whatsapp/module');
            setEnabled(res.data.enabled);
        } catch (err) {
            console.error('Failed to fetch WhatsApp module status', err);
        }
    }, []);

    const fetchStatus = useCallback(async () => {
        try {
            const res = await axiosInstance.get('whatsapp/status');
            setIsReady(res.data.isReady);
            setQr(res.data.qr);
        } catch (err) {
            console.error('Failed to fetch WhatsApp status', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchModuleStatus(); }, [fetchModuleStatus]);

    // Poll every 5 seconds only when module is enabled
    useEffect(() => {
        if (!enabled) { setLoading(false); return; }
        setLoading(true);
        fetchStatus();
        const interval = setInterval(fetchStatus, 5000);
        return () => clearInterval(interval);
    }, [enabled, fetchStatus]);

    const handleToggle = async () => {
        const next = !enabled;
        const action = next ? 'enable' : 'disable';
        const result = await Swal.fire({
            title: `${next ? 'Enable' : 'Disable'} WhatsApp?`,
            text: next
                ? 'This will start the WhatsApp service and begin QR code generation.'
                : 'This will stop the WhatsApp service and disconnect any active session.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: `Yes, ${action}`,
            cancelButtonText: 'Cancel',
        });
        if (!result.isConfirmed) return;

        setToggling(true);
        try {
            await axiosInstance.post('whatsapp/module', { enable: next });
            setEnabled(next);
            if (!next) { setIsReady(false); setQr(null); }
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Failed to update setting.', 'error');
        } finally {
            setToggling(false);
        }
    };

    const handleLogout = async () => {
        const result = await Swal.fire({
            title: 'Change WhatsApp Account?',
            text: 'This will disconnect the current account and show a new QR code to scan.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, logout',
            cancelButtonText: 'Cancel',
        });
        if (!result.isConfirmed) return;

        try {
            await axiosInstance.post('whatsapp/logout');
            setIsReady(false);
            setQr(null);
            setLoading(true);
            Swal.fire('Logged out', 'Scan the new QR code to connect a different account.', 'success');
            setTimeout(fetchStatus, 3000);
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Failed to logout.', 'error');
        }
    };

    return (
        <div className='pc-container'>
            <div className='pc-content'>
                <h5 className='pt-4 mb-5'>
                    <i className="fa fa-arrow-left me-2" onClick={() => navigate(-1)} style={{ cursor: 'pointer' }} />
                    WhatsApp Settings
                </h5>

                <div className='row justify-content-center'>
                    <div className='col-lg-5 col-md-8'>

                        {/* ── Enable / Disable toggle card ── */}
                        <div className='card p-4 mb-3'>
                            <div className='d-flex align-items-center justify-content-between'>
                                <div>
                                    <div className='fw-semibold' style={{ fontSize: 15 }}>WhatsApp Integration</div>
                                    <div className='text-muted' style={{ fontSize: 13 }}>
                                        {enabled
                                            ? 'Service is running. Disable to stop QR generation and free server resources.'
                                            : 'Service is stopped. Enable to start WhatsApp messaging.'}
                                    </div>
                                </div>
                                <div className='form-check form-switch ms-3 mb-0' style={{ transform: 'scale(1.3)', transformOrigin: 'right center' }}>
                                    <input
                                        className='form-check-input'
                                        type='checkbox'
                                        role='switch'
                                        checked={enabled}
                                        onChange={handleToggle}
                                        disabled={toggling}
                                        style={{ cursor: toggling ? 'not-allowed' : 'pointer', width: 44, height: 22 }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* ── Connection status card (only when enabled) ── */}
                        {enabled && (
                            <div className='card p-4 text-center'>

                                {/* Status badge */}
                                <div className='mb-4'>
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 8,
                                        padding: '6px 16px', borderRadius: 20, fontSize: 14, fontWeight: 600,
                                        background: isReady ? '#e6f9ee' : '#fff3e0',
                                        color:      isReady ? '#1a7a3c' : '#b36200',
                                    }}>
                                        <span style={{
                                            width: 10, height: 10, borderRadius: '50%', display: 'inline-block',
                                            background: isReady ? '#25D366' : '#ff9800',
                                        }} />
                                        {loading ? 'Checking...' : isReady ? 'Connected' : 'Not Connected'}
                                    </span>
                                </div>

                                {/* Connected */}
                                {isReady && (
                                    <>
                                        <div className='mb-3'>
                                            <i className='ti ti-brand-whatsapp' style={{ fontSize: 64, color: '#25D366' }} />
                                        </div>
                                        <p className='text-muted mb-4'>
                                            WhatsApp is connected and ready to send appointment notifications.
                                        </p>
                                        <button className='btn btn-outline-danger' onClick={handleLogout}>
                                            <i className='ti ti-logout me-2' />Change Account
                                        </button>
                                    </>
                                )}

                                {/* QR ready to scan */}
                                {!isReady && qr && (
                                    <>
                                        <p className='mb-3 fw-semibold'>Scan this QR code with WhatsApp</p>
                                        <div className='d-flex justify-content-center mb-3'>
                                            <div style={{ padding: 16, background: '#fff', borderRadius: 12, display: 'inline-block', boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}>
                                                <QRCodeSVG value={qr} size={220} />
                                            </div>
                                        </div>
                                        <p className='text-muted' style={{ fontSize: 13 }}>
                                            Open WhatsApp on your phone → Settings → Linked Devices → Link a Device
                                        </p>
                                    </>
                                )}

                                {/* Waiting for QR */}
                                {!isReady && !qr && !loading && (
                                    <>
                                        <p className='text-muted mb-3'>Waiting for QR code to generate...</p>
                                        <div className='spinner-border text-success mx-auto' role='status' />
                                    </>
                                )}

                                {loading && (
                                    <div className='spinner-border text-success mx-auto' role='status' />
                                )}
                            </div>
                        )}

                        {/* Disabled state hint */}
                        {!enabled && (
                            <div className='card p-4 text-center text-muted'>
                                <i className='ti ti-brand-whatsapp mb-2' style={{ fontSize: 48, color: '#ccc' }} />
                                <p className='mb-0' style={{ fontSize: 14 }}>
                                    WhatsApp integration is currently disabled.<br />
                                    Toggle the switch above to enable it.
                                </p>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
};

export default WhatsappSetting;
